import { useCallback, useRef, useState } from 'react';

import type { OnYourHonorReasonId } from '../model/onYourHonor';

type UseOnYourHonorReasonFlowParams = {
  /** Whether this expert is in the cohort that must give a reason. */
  isReasonEnabled: boolean;
  /** False once the inquiry has a persisted answer, so a re-mark never re-asks. */
  shouldPromptReason: () => boolean;
  /**
   * Applies the mark, returning the rotating gratitude line, or null when the
   * toggle didn't land. The flow marks by no other route, which is what makes
   * "closing forgoes the mark" true by construction rather than by a guard.
   */
  applyMark: () => string | null;
  /** Thanks by toast, for marks that never open the sheet. */
  showThanksToast: (line: string) => void;
  trackReasonViewed: () => void;
  trackReasonDismissed: () => void;
  submitReasonChip: (reason: OnYourHonorReasonId) => Promise<unknown> | void;
  submitReasonCustom: (text: string) => Promise<unknown> | void;
};

/**
 * Orchestrates the "reason before the mark" flow for On Your Honor: when the
 * sheet opens, when the mark is applied, and where the expert gets thanked.
 *
 * Extracted from `ChatContent`, which has ~90 imports and cannot be rendered in
 * a unit test, leaving the three behaviors that matter unpinned: closing leaves
 * the inquiry unmarked, choosing a reason marks it, and a second press after a
 * dismissal asks again rather than letting the mark through unasked.
 *
 * Unmarking an already-confirmed inquiry stays in the component, since that path
 * never involves the sheet.
 */
export function useOnYourHonorReasonFlow({
  isReasonEnabled,
  shouldPromptReason,
  applyMark,
  showThanksToast,
  trackReasonViewed,
  trackReasonDismissed,
  submitReasonChip,
  submitReasonCustom,
}: UseOnYourHonorReasonFlowParams) {
  /**
   * Whether the sheet is up. For the reason-required cohort it opens before
   * anything is marked and stays up until a reason is chosen or the expert
   * closes it.
   */
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  /**
   * Gratitude line for the sheet's answered state, or null while the question is
   * still open. Set from the mark the reason applied, so the sheet can thank the
   * expert in place instead of handing off to a toast.
   */
  const [thanksLine, setThanksLine] = useState<string | null>(null);

  /**
   * Outcome of the current showing, so the close handler can tell a genuine
   * dismissal from the close that follows an answer. Every close routes through
   * `closeSheet`, including the programmatic one after the thank-you beat, so
   * without this the dismissed event would double-count every answer.
   */
  const outcomeRef = useRef<'pending' | 'answered' | 'dismissed'>('pending');

  /**
   * Press on an unmarked inquiry. The reason cohort gets the sheet and no mark;
   * everyone else marks immediately and is thanked by toast, having no sheet to
   * be thanked in.
   *
   * The session "already shown" guard is deliberately not set. A dismissal now
   * costs the mark, so the next press has to ask again; registering it as shown
   * would make `shouldPromptReason` false and let the second press through
   * unasked, which is what this cohort exists to prevent.
   */
  const pressUnmarked = useCallback(() => {
    if (isReasonEnabled && shouldPromptReason()) {
      outcomeRef.current = 'pending';

      setThanksLine(null);

      trackReasonViewed();

      setIsSheetOpen(true);

      return;
    }

    const line = applyMark();

    if (line) {
      showThanksToast(line);
    }
  }, [isReasonEnabled, shouldPromptReason, applyMark, showThanksToast, trackReasonViewed]);

  /**
   * Applies the mark a reason earned and hands the line to the sheet, which holds
   * it for a beat then closes itself. Not closed here, or the thank-you would be
   * dropped before it rendered. A toggle that fails to land returns no line, and
   * the sheet closes rather than lingering over an unmarked inquiry.
   */
  const markWithThanks = useCallback(() => {
    outcomeRef.current = 'answered';

    const line = applyMark();

    if (!line) {
      setIsSheetOpen(false);

      return;
    }

    setThanksLine(line);
  }, [applyMark]);

  /**
   * Records a chip choice. The submit is fire-and-forget: the survey hook owns
   * persistence and error reporting, and the mark does not wait on it, so a
   * survey outage never costs the expert credit.
   */
  const selectReason = useCallback(
    (reason: OnYourHonorReasonId) => {
      markWithThanks();

      void submitReasonChip(reason);
    },
    [markWithThanks, submitReasonChip]
  );

  /** Records a written-in reason. Same contract as {@link selectReason}. */

  const customReason = useCallback(
    (text: string) => {
      markWithThanks();

      void submitReasonCustom(text);
    },
    [markWithThanks, submitReasonCustom]
  );

  /**
   * Closes the sheet. The host's X, backdrop, Esc, drag-to-dismiss and the
   * sheet's own post-thanks close all route here, so it is close-only and
   * idempotent: a programmatic close also fires the platform dismiss callback
   * back into this.
   *
   * The dismissed event fires only from `pending`, so closing on the thank-you is
   * not counted as one. Nothing here marks the inquiry, which is the point.
   */
  const closeSheet = useCallback(() => {
    if (outcomeRef.current === 'pending') {
      outcomeRef.current = 'dismissed';

      trackReasonDismissed();
    }

    setIsSheetOpen(false);
  }, [trackReasonDismissed]);

  return { isSheetOpen, thanksLine, pressUnmarked, selectReason, customReason, closeSheet };
}
